/**
 * Self-service billing configuration (AUTONOMY · Layer 1).
 *
 * Closes billing's "backend with endpoint, zero UI" gaps: payment
 * methods enabled per academy (`billing_config methods.enabled`), pricing
 * plans (create/activate/deactivate), discounts/scholarships (create + validity
 * window + deactivate) and receipt footer. All capability-gated on the server
 * (`ManageBilling` ∥ org admin) and audited in activity.
 */
import { type Component, createEffect, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { BadgePercent, CreditCard, Receipt, ScrollText } from "@fleet/icons";

import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { Skeleton, Switch, TextField } from "@fleet/ui";
import { showToast } from "@/components/ui/toast";
import { InfoHint } from "@/components/ui/info-hint";
import { ServerDataGate } from "@/components/ui/server-data-gate";
import { useActiveOrg } from "@/features/academy/hooks/use-active-org";
import { Money } from "./money";
import {
  useBillingConfig,
  useCreateDiscount,
  useCreatePlan,
  useDiscounts,
  useMethods,
  usePlans,
  usePutBillingConfig,
  useUpdateDiscount,
  useUpdatePlan,
} from "../api";

const CENTS = 100;

function parseUsd(raw: string): number | undefined {
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? Math.round(n * CENTS) : undefined;
}

/** Enabled payment methods (registry-driven, with a per-method description). */
const MethodsSection: Component = () => {
  const { t } = useI18n();
  const { activeOrgId } = useActiveOrg();
  const methods = useMethods(activeOrgId);
  const putConfig = usePutBillingConfig();

  const toggle = (key: string, on: boolean) => {
    const current = (methods.data ?? []).filter((m) => m.enabled).map((m) => m.key);
    const next = on ? [...new Set([...current, key])] : current.filter((k) => k !== key);
    if (next.length === 0) {
      showToast(t(msg`At least one payment method must stay enabled`), "error");
      return;
    }
    putConfig.mutate(
      { org: activeOrgId(), key: "methods.enabled", value: next },
      {
        onSuccess: () => showToast(t(msg`Payment methods updated`), "success"),
        onError: (e) => showToast(e.message, "error"),
      },
    );
  };

  return (
    <section class="space-y-3" data-testid="billing-methods-section">
      <div class="flex items-center gap-2">
        <CreditCard class="h-4 w-4 text-primary" />
        <h3 class="text-sm font-bold">{t(msg`Payment methods`)}</h3>
      </div>
      <p class="text-xs text-muted-foreground">
        {t(
          msg`Methods your academy accepts. Disabled methods disappear from the cashier form and the student checkout. Stripe additionally requires the Stripe integration.`,
        )}
      </p>
      <Show when={methods.data} fallback={<Skeleton class="h-24 w-full rounded-lg" />}>
        <div class="grid gap-2 sm:grid-cols-2">
          <For each={methods.data}>
            {(m) => (
              <label class="flex items-center justify-between gap-3 rounded-lg border border-border/40 px-3 py-2">
                <span class="min-w-0">
                  <span class="block text-sm font-medium">{m.name}</span>
                  <span class="block text-xs text-muted-foreground">
                    {m.kind === "online" ? t(msg`Online`) : t(msg`Manual`)} ·{" "}
                    {m.currencies.join(" / ")}
                  </span>
                </span>
                <Switch
                  checked={m.enabled}
                  onCheckedChange={(on) => toggle(m.key, on)}
                  data-testid={`method-toggle-${m.key}`}
                />
              </label>
            )}
          </For>
        </div>
      </Show>
    </section>
  );
};

/** Planes de precio: listar, crear, activar/desactivar. */
const PlansSection: Component = () => {
  const { t } = useI18n();
  const { activeOrgId } = useActiveOrg();
  const plans = usePlans(activeOrgId);
  const createPlan = useCreatePlan();
  const updatePlan = useUpdatePlan();

  const [name, setName] = createSignal("");
  const [kind, setKind] = createSignal("one_time");
  const [scopeType, setScopeType] = createSignal("cohort");
  const [scopeId, setScopeId] = createSignal("");
  const [amount, setAmount] = createSignal("");
  const [installments, setInstallments] = createSignal("");
  const [installmentAmount, setInstallmentAmount] = createSignal("");
  const [fee, setFee] = createSignal("");

  const submit = (e: Event) => {
    e.preventDefault();
    const amountUsdMinor = parseUsd(amount());
    if (!name().trim() || !scopeId().trim() || !amountUsdMinor) {
      showToast(t(msg`Fill name, scope and a valid amount`), "error");
      return;
    }
    createPlan.mutate(
      {
        org: activeOrgId(),
        scopeType: scopeType(),
        scopeId: scopeId().trim(),
        name: name().trim(),
        kind: kind(),
        amountUsdMinor,
        installmentCount:
          kind() === "installments" ? Number(installments()) || undefined : undefined,
        installmentAmountUsdMinor:
          kind() === "installments" ? parseUsd(installmentAmount()) : undefined,
        enrollmentFeeUsdMinor: parseUsd(fee()) ?? 0,
      },
      {
        onSuccess: () => {
          showToast(t(msg`Plan created`), "success");
          setName("");
          setScopeId("");
          setAmount("");
          setInstallments("");
          setInstallmentAmount("");
          setFee("");
        },
        onError: (err) => showToast(err.message, "error"),
      },
    );
  };

  return (
    <section class="space-y-3" data-testid="billing-plans-section">
      <div class="flex items-center gap-2">
        <ScrollText class="h-4 w-4 text-primary" />
        <h3 class="text-sm font-bold">{t(msg`Price plans`)}</h3>
        <InfoHint>
          {t(
            msg`A plan defines what a cohort, program or course costs: one-time, monthly, or split into installments. Enrolling a student generates one invoice per installment. Amounts are not editable after creation (invoices reference them): deactivate and create a new plan instead.`,
          )}
        </InfoHint>
      </div>
      <Show when={plans.data} fallback={<Skeleton class="h-24 w-full rounded-lg" />}>
        <div class="space-y-2">
          <For
            each={plans.data}
            fallback={
              <p class="text-xs text-muted-foreground">
                {t(
                  msg`No plans yet. A price plan is how you charge for a cohort or program — create the first one below.`,
                )}
              </p>
            }
          >
            {(p) => (
              <div class="flex items-center justify-between gap-3 rounded-lg border border-border/40 px-3 py-2">
                <div class="min-w-0">
                  <p class="truncate text-sm font-medium">
                    {p.name} <span class="text-xs text-muted-foreground">({p.kind})</span>
                  </p>
                  <p class="truncate text-xs text-muted-foreground">
                    <Money usdMinor={p.amountUsdMinor} />
                    <Show when={p.installmentCount}>
                      {" · "}
                      {p.installmentCount}× <Money usdMinor={p.installmentAmountUsdMinor ?? 0} />
                    </Show>
                    {" · "}
                    {p.scopeType}:{p.scopeId}
                  </p>
                </div>
                <Switch
                  checked={p.active}
                  onCheckedChange={(on) =>
                    updatePlan.mutate(
                      { id: p.id, active: on },
                      { onError: (err) => showToast(err.message, "error") },
                    )
                  }
                  data-testid={`plan-active-${p.id}`}
                />
              </div>
            )}
          </For>
        </div>
      </Show>

      <form
        onSubmit={submit}
        class="grid gap-2 rounded-lg border border-border/40 p-3 sm:grid-cols-2"
      >
        <TextField
          placeholder={t(msg`Plan name`)}
          value={name()}
          onInput={(e) => setName(e.currentTarget.value)}
          data-testid="plan-name"
        />
        <Select
          aria-label={t(msg`Kind`)}
          value={kind()}
          onChange={setKind}
          options={[
            { value: "one_time", label: t(msg`One-time`) },
            { value: "installments", label: t(msg`Installments`) },
            { value: "monthly", label: t(msg`Monthly`) },
          ]}
        />
        <Select
          aria-label={t(msg`Scope`)}
          value={scopeType()}
          onChange={setScopeType}
          options={[
            { value: "cohort", label: t(msg`Cohort`) },
            { value: "program", label: t(msg`Program`) },
            { value: "course", label: t(msg`Course`) },
          ]}
        />
        <TextField
          placeholder={t(msg`Scope id (cohort/program id)`)}
          value={scopeId()}
          onInput={(e) => setScopeId(e.currentTarget.value)}
        />
        <TextField
          type="number"
          inputmode="decimal"
          placeholder={t(msg`Total USD`)}
          value={amount()}
          onInput={(e) => setAmount(e.currentTarget.value)}
          data-testid="plan-amount"
        />
        <TextField
          type="number"
          inputmode="decimal"
          placeholder={t(msg`Enrollment fee USD (optional)`)}
          value={fee()}
          onInput={(e) => setFee(e.currentTarget.value)}
        />
        <Show when={kind() === "installments"}>
          <TextField
            type="number"
            inputmode="numeric"
            placeholder={t(msg`Number of installments`)}
            value={installments()}
            onInput={(e) => setInstallments(e.currentTarget.value)}
          />
          <TextField
            type="number"
            inputmode="decimal"
            placeholder={t(msg`Amount per installment USD`)}
            value={installmentAmount()}
            onInput={(e) => setInstallmentAmount(e.currentTarget.value)}
          />
        </Show>
        <Button
          type="submit"
          class="sm:col-span-2"
          disabled={createPlan.isPending}
          data-testid="plan-create"
        >
          {t(msg`Create plan`)}
        </Button>
      </form>
    </section>
  );
};

/** Discounts and scholarships: list, create with validity window, deactivate. */
const DiscountsSection: Component = () => {
  const { t } = useI18n();
  const { activeOrgId } = useActiveOrg();
  const discounts = useDiscounts(activeOrgId);
  const createDiscount = useCreateDiscount();
  const updateDiscount = useUpdateDiscount();

  const [kind, setKind] = createSignal("promo_code");
  const [code, setCode] = createSignal("");
  const [mode, setMode] = createSignal<"percent" | "amount">("percent");
  const [value, setValue] = createSignal("");
  const [maxUses, setMaxUses] = createSignal("");
  const [validFrom, setValidFrom] = createSignal("");
  const [validTo, setValidTo] = createSignal("");

  const submit = (e: Event) => {
    e.preventDefault();
    const numeric = Number(value());
    if (!Number.isFinite(numeric) || numeric <= 0) {
      showToast(t(msg`Enter a valid discount value`), "error");
      return;
    }
    createDiscount.mutate(
      {
        org: activeOrgId(),
        kind: kind(),
        code: code().trim() || undefined,
        percent: mode() === "percent" ? numeric : undefined,
        amountUsdMinor: mode() === "amount" ? Math.round(numeric * CENTS) : undefined,
        maxUses: Number(maxUses()) || undefined,
        validFrom: validFrom() || undefined,
        validTo: validTo() || undefined,
      },
      {
        onSuccess: () => {
          showToast(t(msg`Discount created`), "success");
          setCode("");
          setValue("");
          setMaxUses("");
          setValidFrom("");
          setValidTo("");
        },
        onError: (err) => showToast(err.message, "error"),
      },
    );
  };

  return (
    <section class="space-y-3" data-testid="billing-discounts-section">
      <div class="flex items-center gap-2">
        <BadgePercent class="h-4 w-4 text-primary" />
        <h3 class="text-sm font-bold">{t(msg`Discounts and scholarships`)}</h3>
        <InfoHint>
          {t(
            msg`Promo codes are applied by students or staff at enrollment; scholarships are assigned by the academy. Both reduce generated invoices by a percent or a fixed USD amount, within the validity window and usage cap.`,
          )}
        </InfoHint>
      </div>
      <Show when={discounts.data} fallback={<Skeleton class="h-24 w-full rounded-lg" />}>
        <div class="space-y-2">
          <For
            each={discounts.data}
            fallback={
              <p class="text-xs text-muted-foreground">
                {t(
                  msg`No discounts yet. Create a promo code for campaigns or a scholarship for specific students.`,
                )}
              </p>
            }
          >
            {(d) => (
              <div class="flex items-center justify-between gap-3 rounded-lg border border-border/40 px-3 py-2">
                <div class="min-w-0">
                  <p class="truncate text-sm font-medium">
                    {d.code ?? t(msg`(no code)`)}{" "}
                    <span class="text-xs text-muted-foreground">({d.kind})</span>
                  </p>
                  <p class="truncate text-xs text-muted-foreground">
                    <Show
                      when={d.percent != null}
                      fallback={<Money usdMinor={d.amountUsdMinor ?? 0} />}
                    >
                      {d.percent}%
                    </Show>
                    <Show when={d.maxUses != null}>
                      {" · "}
                      {d.usedCount}/{d.maxUses}
                    </Show>
                    <Show when={d.validFrom || d.validTo}>
                      {" · "}
                      {d.validFrom ?? "…"} → {d.validTo ?? "…"}
                    </Show>
                  </p>
                </div>
                <Switch
                  checked={d.active}
                  onCheckedChange={(on) =>
                    updateDiscount.mutate(
                      { id: d.id, active: on },
                      { onError: (err) => showToast(err.message, "error") },
                    )
                  }
                  data-testid={`discount-active-${d.id}`}
                />
              </div>
            )}
          </For>
        </div>
      </Show>

      <form
        onSubmit={submit}
        class="grid gap-2 rounded-lg border border-border/40 p-3 sm:grid-cols-2"
      >
        <Select
          aria-label={t(msg`Kind`)}
          value={kind()}
          onChange={setKind}
          options={[
            { value: "promo_code", label: t(msg`Promo code`) },
            { value: "scholarship", label: t(msg`Scholarship`) },
          ]}
        />
        <TextField
          placeholder={t(msg`Code (e.g. ESPACIO10)`)}
          value={code()}
          onInput={(e) => setCode(e.currentTarget.value.toUpperCase())}
          data-testid="discount-code"
        />
        <Select
          aria-label={t(msg`Mode`)}
          value={mode()}
          onChange={(v) => {
            setMode(v === "amount" ? "amount" : "percent");
          }}
          options={[
            { value: "percent", label: t(msg`Percent (%)`) },
            { value: "amount", label: t(msg`Fixed amount (USD)`) },
          ]}
        />
        <TextField
          type="number"
          inputmode="decimal"
          placeholder={t(msg`Value`)}
          value={value()}
          onInput={(e) => setValue(e.currentTarget.value)}
          data-testid="discount-value"
        />
        <TextField
          type="number"
          inputmode="numeric"
          placeholder={t(msg`Max uses (optional)`)}
          value={maxUses()}
          onInput={(e) => setMaxUses(e.currentTarget.value)}
        />
        <div class="grid grid-cols-2 gap-2">
          <TextField
            type="date"
            aria-label={t(msg`Valid from`)}
            value={validFrom()}
            onInput={(e) => setValidFrom(e.currentTarget.value)}
          />
          <TextField
            type="date"
            aria-label={t(msg`Valid to`)}
            value={validTo()}
            onInput={(e) => setValidTo(e.currentTarget.value)}
          />
        </div>
        <Button
          type="submit"
          class="sm:col-span-2"
          disabled={createDiscount.isPending}
          data-testid="discount-create"
        >
          {t(msg`Create discount`)}
        </Button>
      </form>
    </section>
  );
};

/** Pie de recibo (billing_config `receipt.footer`). */
const ReceiptSection: Component = () => {
  const { t } = useI18n();
  const { activeOrgId } = useActiveOrg();
  const config = useBillingConfig(activeOrgId);
  const putConfig = usePutBillingConfig();
  const [footer, setFooter] = createSignal("");

  createEffect(() => {
    const value = config.data?.["receipt.footer"];
    setFooter(typeof value === "string" ? value : "");
  });

  const save = () => {
    putConfig.mutate(
      { org: activeOrgId(), key: "receipt.footer", value: footer().trim() },
      {
        onSuccess: () => showToast(t(msg`Receipt footer saved`), "success"),
        onError: (e) => showToast(e.message, "error"),
      },
    );
  };

  return (
    <section class="space-y-3" data-testid="billing-receipt-section">
      <div class="flex items-center gap-2">
        <Receipt class="h-4 w-4 text-primary" />
        <h3 class="text-sm font-bold">{t(msg`Printable receipts`)}</h3>
      </div>
      <p class="text-xs text-muted-foreground">
        {t(
          msg`Footer line printed on every receipt (tax info, thank-you note). The academy name on receipts comes from Branding in your organization settings.`,
        )}
      </p>
      <TextField
        placeholder={t(msg`e.g. RIF J-12345678-9 · Gracias por su pago`)}
        value={footer()}
        onInput={(e) => setFooter(e.currentTarget.value)}
        data-testid="receipt-footer-input"
      />
      <Button onClick={save} disabled={putConfig.isPending} data-testid="receipt-footer-save">
        {t(msg`Save footer`)}
      </Button>
    </section>
  );
};

/** Full billing configuration panel. */
export const BillingSettings: Component = () => {
  const { activeOrgId } = useActiveOrg();
  // LF-B ServerDataGate: pantalla server-only (REST) — offline honesto y error
  // with retry instead of an eternal skeleton. The methods query is the
  // primary one (first section); the rest carry their own skeleton.
  const methods = useMethods(activeOrgId);
  return (
    <ServerDataGate
      pending={methods.isLoading}
      error={methods.isError}
      refetch={() => void methods.refetch()}
      skeleton={<Skeleton class="h-64 w-full rounded-2xl" />}
    >
      <div class="space-y-8" data-testid="billing-settings">
        <MethodsSection />
        <PlansSection />
        <DiscountsSection />
        <ReceiptSection />
      </div>
    </ServerDataGate>
  );
};
